import {ReactComponent as Illustration} from '@hconnect/uikit/src/lib2/pages/illustration500.svg'
import {
  Card,
  CardContent,
  CardHeader,
  Grid,
  GridProps,
  LinearProgress,
  SxProps,
  Theme
} from '@mui/material'
import React from 'react'
import {ErrorBoundary} from 'react-error-boundary'

export interface DashboardWidgetProps {
  title: React.ReactNode
  children: React.ReactNode
  gridProps?: Omit<GridProps, 'item' | 'zeroMinWidth'>
  minWidth?: number | string
  maxHeight?: number | string
  cardSx?: SxProps<Theme>
  headerAction?: React.ReactNode
  loading?: boolean
  onClick?: React.MouseEventHandler
  'data-test-id'?: string
}

const DefaultErrorFallback = () => <Illustration style={{width: '100%'}} viewBox="0 0 401 316" />

export const DashboardWidget = ({
  title,
  children,
  gridProps,
  minWidth,
  maxHeight,
  cardSx,
  headerAction,
  loading,
  onClick,
  'data-test-id': dataTestId
}: DashboardWidgetProps) => {
  // Skip onClick if click originated from an interactive child element
  const handleClick: React.MouseEventHandler = (e) => {
    if (!onClick) return
    const target = e.target as HTMLElement
    if (target.closest('button, a, [role="button"], .MuiCardActionArea-root, input, select')) {
      return
    }
    onClick(e)
  }

  return (
    <Grid item zeroMinWidth {...gridProps}>
      <Card
        onClick={handleClick}
        data-test-id={dataTestId}
        sx={{
          padding: 3,
          height: '100%',
          display: 'flex',
          gap: 3,
          flexDirection: 'column',
          minWidth,
          maxHeight,
          cursor: onClick ? 'pointer' : 'default',
          ...cardSx
        }}
      >
        <CardHeader
          action={headerAction}
          title={title}
          titleTypographyProps={{variant: 'h3'}}
          sx={{
            padding: 0,
            alignItems: 'center',
            '& .MuiCardHeader-content': {
              minWidth: 0,
              wordBreak: 'break-word'
            },
            '& .MuiCardHeader-action': {
              flexShrink: 0,
              alignSelf: 'flex-start'
            }
          }}
        />

        <CardContent
          sx={{
            padding: 0,
            textAlign: 'center',
            overflow: 'auto',
            flex: 1
          }}
        >
          {loading ? <LinearProgress /> : <div style={{height: '4px'}} />}
          <ErrorBoundary fallback={<DefaultErrorFallback />}>{children}</ErrorBoundary>
        </CardContent>
      </Card>
    </Grid>
  )
}
